import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { toast } from 'sonner';
import { loadAPI } from '../../api';
import { buildRateConfirmationHtml, fmtDate } from './rateConfirmationHtml';

const num = (v) => (v === null || v === undefined || v === '' ? '' : Number(v).toLocaleString('en-US'));

const buildInitial = (load, facilities, carriers, accessorials) => {
  const saved = load.rc_data || {};
  const carrier = carriers.find(c => c.id === load.carrier_id);
  const stops = (load.stops || []).map((s, i) => {
    const f = facilities.find(x => x.id === s.facility_id) || {};
    const prev = (saved.stops || [])[i] || {};
    const addr = [s.address || f.address, [s.city || f.city, s.state || f.state].filter(Boolean).join(', '), s.zip_code || f.zip_code].filter(Boolean).join(', ');
    const refs = [s.po_number, s.delivery_number, ...(s.references || [])].filter(Boolean).join(', ');
    return {
      type: s.type,
      name: s.facility_name || f.name || '',
      address: addr,
      contact: prev.contact ?? '',
      phone: prev.phone ?? (f.contact_phone || f.scheduling_phone || ''),
      reference: prev.reference ?? refs,
      miles: prev.miles ?? '',
      instructions: prev.instructions ?? (f.scheduling_instructions || ''),
      appointment: s.appointment_datetime || null,
      appointmentEnd: s.appointment_end_time || '',
    };
  });
  const pickups = (load.stops || []).filter(s => s.type === 'pickup');
  const deliveries = (load.stops || []).filter(s => s.type === 'delivery');
  const firstPU = pickups.map(s => s.appointment_datetime).filter(Boolean).sort()[0];
  const lastDel = deliveries.map(s => s.appointment_datetime).filter(Boolean).sort().slice(-1)[0];
  const temp = (load.temperature || '').toString().trim();
  const rateItems = saved.rateItems || [
    { description: 'Line Haul', amount: parseFloat(load.carrier_line_haul) || 0 },
    ...accessorials.filter(a => parseFloat(a.carrier_amount) > 0).map(a => ({ description: a.description || 'Accessorial', amount: parseFloat(a.carrier_amount) })),
  ];
  return {
    loadNumber: load.load_number || '',
    date: fmtDate(new Date().toISOString()),
    carrierName: load.carrier_name || carrier?.name || '',
    mcNumber: carrier?.mc_number || '',
    carrierContact: saved.carrierContact ?? (load.dispatch_name || carrier?.contact_name || ''),
    carrierPhone: saved.carrierPhone ?? (load.dispatch_phone || carrier?.contact_phone || ''),
    driverName: load.driver_name || '',
    driverPhone: load.driver_phone || '',
    truckNumber: load.truck_number || '',
    trailerNumber: load.trailer_number || '',
    readyDate: saved.readyDate ?? fmtDate(firstPU || load.shipping_date),
    dateNeeded: saved.dateNeeded ?? fmtDate(lastDel || load.delivery_date),
    serviceLevel: saved.serviceLevel ?? 'Normal',
    temperatureRange: saved.temperatureRange ?? (temp ? `${temp}F to ${temp}F` : 'N/A'),
    customerPO: saved.customerPO ?? deliveries.map(s => s.po_number).filter(Boolean).join(', '),
    shipperRef: saved.shipperRef ?? pickups.flatMap(s => s.references || []).filter(Boolean).join(', '),
    trailerType: saved.trailerType ?? (load.equipment ? `53 ft ${load.equipment} / Full` : ''),
    reeferMode: load.reefer_mode || 'N/A',
    handlingUnits: num(load.pallet_count),
    packageType: saved.packageType ?? 'Pallet',
    commodity: load.commodity || '',
    totalWeight: num(load.weight),
    rateItems,
    notes: saved.notes ?? '',
    stops,
  };
};

const Row = ({ label, children }) => (
  <div className="lw-row">
    <span className="lw-label w110">{label}</span>
    {children}
  </div>
);

export const RateConfirmationDialog = ({ open, onOpenChange, loadId, load, facilities, carriers, accessorials, onSaved }) => {
  const [rc, setRc] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && load) setRc(buildInitial(load, facilities, carriers, accessorials));
  }, [open, load, facilities, carriers, accessorials]);

  if (!rc) return null;

  const set = (k) => (e) => setRc(prev => ({ ...prev, [k]: e.target.value }));
  const setStop = (i, k, v) => setRc(prev => ({ ...prev, stops: prev.stops.map((s, idx) => idx === i ? { ...s, [k]: v } : s) }));
  const setItem = (i, k, v) => setRc(prev => ({ ...prev, rateItems: prev.rateItems.map((r, idx) => idx === i ? { ...r, [k]: v } : r) }));
  const total = rc.rateItems.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);

  const persist = async () => {
    const { stops, rateItems, notes, serviceLevel, readyDate, dateNeeded, temperatureRange, customerPO, shipperRef, trailerType, packageType, carrierContact, carrierPhone } = rc;
    const rc_data = {
      stops: stops.map(({ contact, phone, reference, miles, instructions }) => ({ contact, phone, reference, miles, instructions })),
      rateItems, notes, serviceLevel, readyDate, dateNeeded, temperatureRange, customerPO, shipperRef, trailerType, packageType, carrierContact, carrierPhone,
      generated_at: new Date().toISOString(),
    };
    await loadAPI.update(loadId, { rc_data });
    onSaved && onSaved(rc_data);
  };

  const handleGenerate = async () => {
    if (!rc.carrierName) { toast.error('Assign a carrier before creating the Rate Confirmation'); return; }
    setSaving(true);
    try {
      await persist();
      const blob = new Blob([buildRateConfirmationHtml(rc)], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      const w = window.open(url, '_blank');
      setTimeout(() => { if (w) w.print(); URL.revokeObjectURL(url); }, 600);
      toast.success('Rate Confirmation generated');
    } catch {
      toast.error('Failed to generate Rate Confirmation');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto lw-root" data-testid="lw-rc-modal">
        <DialogHeader>
          <DialogTitle>Rate Confirmation — Load# {rc.loadNumber}</DialogTitle>
          <DialogDescription className="sr-only">Review and generate the carrier rate confirmation</DialogDescription>
        </DialogHeader>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <div>
            <div style={{ fontWeight: 800, fontSize: 12, marginBottom: 4 }}>Carrier</div>
            <Row label="Carrier"><input className="lw-input" value={rc.carrierName} readOnly data-testid="rc-carrier-name" /></Row>
            <Row label="MC#"><input className="lw-input narrow" value={rc.mcNumber} readOnly data-testid="rc-mc" /></Row>
            <Row label="Contact"><input className="lw-input" value={rc.carrierContact} onChange={set('carrierContact')} data-testid="rc-carrier-contact" /></Row>
            <Row label="Phone"><input className="lw-input narrow" value={rc.carrierPhone} onChange={set('carrierPhone')} data-testid="rc-carrier-phone" /></Row>
            <Row label="Ready Date"><input className="lw-input narrow" value={rc.readyDate} onChange={set('readyDate')} data-testid="rc-ready-date" /></Row>
            <Row label="Date Needed"><input className="lw-input narrow" value={rc.dateNeeded} onChange={set('dateNeeded')} data-testid="rc-date-needed" /></Row>
            <Row label="Service Level">
              <select className="lw-input narrow" value={rc.serviceLevel} onChange={set('serviceLevel')} data-testid="rc-service-level">
                {['Normal', 'Expedited', 'Team', 'Hot'].map(o => <option key={o}>{o}</option>)}
              </select>
            </Row>
            <Row label="Temp Range"><input className="lw-input narrow" value={rc.temperatureRange} onChange={set('temperatureRange')} data-testid="rc-temp" /></Row>
          </div>
          <div>
            <div style={{ fontWeight: 800, fontSize: 12, marginBottom: 4 }}>Shipment</div>
            <Row label="Customer PO"><input className="lw-input" value={rc.customerPO} onChange={set('customerPO')} data-testid="rc-customer-po" /></Row>
            <Row label="Shipper Ref"><input className="lw-input" value={rc.shipperRef} onChange={set('shipperRef')} data-testid="rc-shipper-ref" /></Row>
            <Row label="Trailer Type"><input className="lw-input" value={rc.trailerType} onChange={set('trailerType')} data-testid="rc-trailer-type" /></Row>
            <Row label="Reefer Mode"><input className="lw-input narrow" value={rc.reeferMode} readOnly /></Row>
            <Row label="Package Type"><input className="lw-input narrow" value={rc.packageType} onChange={set('packageType')} data-testid="rc-package-type" /></Row>
            <Row label="Commodity"><input className="lw-input" value={rc.commodity} readOnly /></Row>
            <Row label="Units / Weight"><input className="lw-input tiny" value={rc.handlingUnits} readOnly /><input className="lw-input tiny" style={{ marginLeft: 6 }} value={rc.totalWeight} readOnly /></Row>
          </div>
        </div>

        <div className="lw-sep" />
        <div style={{ fontWeight: 800, fontSize: 12, marginBottom: 4 }}>Stops</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          {rc.stops.map((s, i) => (
            <div key={i} style={{ border: '1px solid #e3e7ef', borderRadius: 4, padding: '6px 8px' }} data-testid={`rc-stop-${i}`}>
              <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 2 }}>
                {i + 1}. {s.type === 'pickup' ? 'Pickup' : 'Delivery'} — {s.name || 'N/A'}
              </div>
              <div style={{ fontSize: 11, color: '#686868', marginBottom: 4 }}>{s.address || 'No address'}</div>
              <Row label="Reference #"><input className="lw-input" value={s.reference} onChange={(e) => setStop(i, 'reference', e.target.value)} data-testid={`rc-stop-ref-${i}`} /></Row>
              <Row label="Contact / Phone">
                <input className="lw-input" value={s.contact} onChange={(e) => setStop(i, 'contact', e.target.value)} placeholder="Contact" data-testid={`rc-stop-contact-${i}`} />
                <input className="lw-input narrow" style={{ marginLeft: 6 }} value={s.phone} onChange={(e) => setStop(i, 'phone', e.target.value)} placeholder="Phone" data-testid={`rc-stop-phone-${i}`} />
              </Row>
              <Row label="Miles to Next"><input className="lw-input tiny" value={s.miles} onChange={(e) => setStop(i, 'miles', e.target.value)} data-testid={`rc-stop-miles-${i}`} /></Row>
              <Row label="Instructions">
                <textarea className="lw-input" rows={2} style={{ height: 'auto', resize: 'vertical' }} value={s.instructions} onChange={(e) => setStop(i, 'instructions', e.target.value)} data-testid={`rc-stop-instructions-${i}`} />
              </Row>
            </div>
          ))}
        </div>

        <div className="lw-sep" />
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
          <span style={{ fontWeight: 800, fontSize: 12 }}>Rate Breakdown (Carrier)</span>
          <button className="lw-btn" style={{ height: 24 }} onClick={() => setRc(prev => ({ ...prev, rateItems: [...prev.rateItems, { description: '', amount: 0 }] }))} data-testid="rc-add-rate-item">+ Line</button>
        </div>
        {rc.rateItems.map((r, i) => (
          <div className="lw-row" key={i}>
            <input className="lw-input" value={r.description} onChange={(e) => setItem(i, 'description', e.target.value)} placeholder="Description" data-testid={`rc-rate-desc-${i}`} />
            <input className="lw-input narrow" type="number" step="0.01" value={r.amount} onChange={(e) => setItem(i, 'amount', e.target.value)} style={{ textAlign: 'right' }} data-testid={`rc-rate-amount-${i}`} />
            <button className="lw-btn" style={{ height: 24, padding: '0 8px' }} onClick={() => setRc(prev => ({ ...prev, rateItems: prev.rateItems.filter((_, idx) => idx !== i) }))} title="Remove" data-testid={`rc-rate-remove-${i}`}>×</button>
          </div>
        ))}
        <div style={{ textAlign: 'right', fontWeight: 800, fontSize: 13, paddingRight: 44 }} data-testid="rc-total">
          TOTAL: ${total.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </div>

        <Row label="Notes">
          <textarea className="lw-input" rows={2} style={{ height: 'auto', resize: 'vertical' }} value={rc.notes} onChange={set('notes')} placeholder="Optional notes printed on the RC" data-testid="rc-notes" />
        </Row>

        <div className="lw-btnrow" style={{ justifyContent: 'flex-end' }}>
          <button className="lw-btn" style={{ height: 32 }} onClick={() => onOpenChange(false)} data-testid="rc-cancel">Cancel</button>
          <button className="lw-btn primary" style={{ height: 32 }} onClick={handleGenerate} disabled={saving} data-testid="rc-generate">
            {saving ? 'Generating…' : 'Generate PDF'}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
